import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import DynamicTable from "../../components/Table/DataTable";
import { IconEdit, IconEye } from "../../components/icons/icons";
import TablePaginationActions from "../../components/Table/tablePagination";
import LoadingScreen from "../../components/LoadingScreen";
import apiClient from "../../services/apiClient";
import { PetOrderDialog } from "../PetService/PetOrderDialog";
import { ToggleButtonGroup } from "@mui/material";
import { StyledToggleButton } from "./TodayAppointments";
import FullCalendar from "@fullcalendar/react";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import { formatDate } from "@fullcalendar/core";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import SocketService from "../websocket/SocketService";
const PetServices = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);

  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editingOrder, setEditingOrder] = useState<any>(null);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [currentView, setCurrentView] = useState<"list" | "calendar" | "add">(
    "list"
  );
  const [serviceProviders, setServiceProviders] = useState<any[]>([]);
  const [patients, setpatients] = useState<any[]>([]);

  const [searchField, setSearchField] = useState("searchQuery");
  const [searchValue, setSearchValue] = useState("");
  const [fromDateValue, setFromDateValue] = useState("");
  const [toDateValue, setToDateValue] = useState("");

  const events = orders.map((order) => ({
    id: order.id,
    title: order.pet?.additional_info?.name || "No Name",
    start: order.scheduled_at,
    color: order.service_status?.bg_color || "#1976d2",
    textColor: order.service_status?.text_color || "#fff",
  }));
  const fetchServiceProviders = async () => {
    try {
      const response: any = await apiClient(
        "/v1/resource/service_provider/all",
        {
          method: "GET",
        }
      );
      const providers = response?.data?.service_providers || [];
      setServiceProviders(providers);
    } catch (err) {
      console.error("Failed to fetch service providers:", err);
    }
  };
  const fetchpetowners = async () => {
    try {
      const response: any = await apiClient("/v1/resource/patients/all", {
        method: "GET",
      });
      const patients = response?.data?.patients || [];
      setpatients(patients);
    } catch (err) {
      console.error("Failed to fetch service providers:", err);
    }
  };
  useEffect(() => {
    fetchServiceProviders();
    fetchpetowners();
  }, []);
  const fetchOrders = useCallback(
    async (forcedValue?: string, forcedField?: string) => {
      setLoading(true);
      setError("");

      try {
        const value = forcedValue !== undefined ? forcedValue : searchValue;
        const field = forcedField || searchField;

        const params: any = {
          limit: rowsPerPage,
          page,
          order_by: "id",
          dir: 2,
        };

        let useAllEndpoint = false;

        switch (field) {
          case "orderId":
            if (value) params.id = value;
            break;
          case "serviceProviderId":
            if (value) params.service_provider_id = value;
            break;
          case "parentId":
            if (value) params.user_id = value;
            break;
          case "statusSlug":
            if (value) params.status = value;
            break;
          case "dateRange":
            if (fromDateValue)
              params.scheduled_from = `${fromDateValue} 00:00:00`;
            if (toDateValue) params.scheduled_to = `${toDateValue} 23:59:59`;
            break;
          default:
            if (value) {
              params.searchkey = value;
              useAllEndpoint = true;
            }
            break;
        }

        const queryString = new URLSearchParams(params).toString();
        const endpoint = useAllEndpoint
          ? `v1/resource/ServiceOrder/all?${queryString}`
          : `v1/resource/ServiceOrder?${queryString}`;

        const response: any = await apiClient(endpoint, { method: "GET" });

        setOrders(response.data.service_orders || []);
        setTotalCount(response.data.pagination?.total || 0);
      } catch (err: any) {
        setError(err.message || "Something went wrong");
      } finally {
        setLoading(false);
      }
    },
    [page, rowsPerPage, searchValue, searchField, fromDateValue, toDateValue]
  );

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  useEffect(() => {
    const sub = SocketService._listen<any>("service-order-created").subscribe(
      (newOrder) => {
        console.log("New service order received:", newOrder);
        setOrders((prev) => {
          if (prev.some((o) => o.id === newOrder.id)) return prev;
          return [newOrder, ...prev];
        });
      }
    );

    return () => sub.unsubscribe();
  }, []);

  // Listen for updates on each order
  useEffect(() => {
    if (!orders.length) return;
    const subs = orders.map((order) =>
      SocketService.serviceOrderListener(order.id).subscribe((updated) => {
        console.log("Order updated:", updated);
        setOrders((prev) =>
          prev.map((o) => (o.id === updated.id ? updated : o))
        );
      })
    );

    return () => subs.forEach((s) => s.unsubscribe());
  }, [orders]);

  const handlePageChange = (newPage: number) => {
    setPage(newPage);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setPage(0);
  };

  const handleEdit = (row: any) => {
    setEditingOrder(row);
    setOpenEditDialog(true);
  };

  const handleShowDetails = (row: any) => {
    navigate(`/pet-service-orders/${row.id}`);
  };

  const handleSaveOrder = async (payload: Record<string, any>) => {
    try {
      console.log("Payload to save:", payload);

      if (payload.id) {
        // Update existing order
        await apiClient(`/v1/resource/ServiceOrder/${payload.id}`, {
          method: "PATCH",
          body: payload,
        });
      } else {
        // Create new order
        await apiClient(`/v1/resource/ServiceOrder`, {
          method: "POST",
          body: payload,
        });
      }

      // Refresh the UI
      fetchOrders();
    } catch (err) {
      console.error("Save failed", err);
    } finally {
      setOpenEditDialog(false);
      setOpenAddDialog(false);
    }
  };

  const columns = [
    {
      key: "serial",
      label: "S.No",
      align: "center",
      render: (_row: any, index: number) => (
        <span className="font-medium text-gray-700">
          {page * rowsPerPage + index + 1}
        </span>
      ),
    },
    {
      key: "orderid",
      label: "Order Id",
      align: "center",
      render: (_row: any, index: number) => (
        <span className="font-medium text-gray-700">{_row?.id}</span>
      ),
    },
    {
      key: "date",
      label: "Date & Time",
      align: "left",
      render: (row: any) => {
        if (!row.scheduled_at)
          return (
            <span className="text-gray-400 italic text-sm">Not Available</span>
          );
        const date = new Date(row.scheduled_at);
        if (isNaN(date.getTime()))
          return (
            <span className="text-gray-400 italic text-sm">Invalid Date</span>
          );

        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        let hours = date.getHours();
        const minutes = String(date.getMinutes()).padStart(2, "0");
        const ampm = hours >= 12 ? "PM" : "AM";
        hours = hours % 12 || 12;

        return (
          <div className="flex flex-col space-y-0.5">
            <span className="font-medium text-gray-800 text-sm">{`${day}-${month}-${year}`}</span>
            <span className="text-gray-500 text-xs">{`${hours}:${minutes} ${ampm}`}</span>
          </div>
        );
      },
    },
    {
      key: "pet",
      label: "Pet",
      align: "left",
      render: (row: any) =>
        row?.pet?.additional_info?.name ? (
          <div className="flex items-center space-x-2">
            <span className="text-gray-800 font-medium text-sm">
              {row.pet.additional_info.name}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 italic text-sm">Not Available</span>
        ),
    },
    {
      key: "parent",
      label: "Parent",
      align: "left",
      render: (row: any) =>
        row?.owner?.first_name ? (
          <div className="flex items-center space-x-2">
            <span className="text-gray-800 font-medium text-sm">
              {`${row.owner.first_name} ${row.owner.last_name}`.trim() || "-"}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 italic text-sm">Not Available</span>
        ),
    },
    // {
    //     key: "mobile",
    //     label: "Mobile Number",
    //     align: "left",
    //     render: (row: any) =>
    //         row?.owner?.first_name ? (
    //             <div className="flex items-center space-x-2">
    //                 <span className="text-gray-800 font-medium text-sm">
    //                     {`${row.owner.mobile}`.trim() || "-"}
    //                 </span>
    //             </div>
    //         ) : (
    //             <span className="text-gray-400 italic text-sm">Not Available</span>
    //         ),
    // },
    {
      key: "service",
      label: "Service Name",
      align: "left",
      render: (row: any) =>
        row?.service_charge_code?.additional_info?.service_name ? (
          <span className="bg-lime-200 text-lime-900 px-2 py-1 rounded font-semibold">
            {row.service_charge_code.additional_info.service_name}
          </span>
        ) : (
          <span className="text-gray-400 italic text-sm">Not Available</span>
        ),
    },
    {
      key: "provider",
      label: "Service Provider",
      align: "left",
      render: (row: any) =>
        row?.service_provider?.user?.first_name ? (
          <div className="flex items-center space-x-2">
            <span className="text-gray-800 font-medium text-sm">
              {`${row.service_provider.user.first_name} ${row.service_provider.user.last_name}`.trim() ||
                "-"}
            </span>
          </div>
        ) : (
          <span className="text-gray-400 italic text-sm">Not Available</span>
        ),
    },
    {
      key: "totalamount",
      label: "Total Amount",
      align: "center",
      render: (row: any) =>
        row.total_amount ? (
          <span className="bg-gradient-to-r from-amber-100 to-yellow-100 text-amber-800 px-4 py-1.5 rounded-lg font-bold text-sm shadow-sm border border-amber-200">
            ₹{row.total_amount.toLocaleString("en-IN")}
          </span>
        ) : (
          <span className="text-gray-400 italic text-sm">Not Available</span>
        ),
    },
    {
      key: "Status",
      label: "Status",
      align: "center",
      render: (row: any) => {
        const statusObj = row.service_status || {
          name: "Unknown",
          bg_color: "#E5E7EB",
          text_color: "#374151",
        };
        const formattedName = statusObj.name
          .split(" ")
          .map((w: string) => w.charAt(0).toUpperCase() + w.slice(1))
          .join(" ");
        return (
          <span
            className="inline-block px-4 py-1.5 rounded-full text-xs font-semibold shadow-md tracking-wide"
            style={{
              backgroundColor: statusObj.bg_color,
              color: statusObj.text_color,
              border: `1px solid ${statusObj.text_color}25`,
            }}
          >
            {formattedName}
          </span>
        );
      },
    },
  ];

  const actions = [
    {
      label: "View",
      color: "#2196f3",
      icon: <IconEye size={20} />,
      onClick: handleShowDetails,
    },
    {
      label: "Edit",
      color: "#4caf50",
      icon: <IconEdit size={20} />,
      onClick: handleEdit,
    },
  ];

  if (loading)
    return <LoadingScreen message="Loading Dashboard..." layout={false} />;
  if (error)
    return (
      <div className="text-center mt-10 text-red-500 font-semibold">
        {error}
      </div>
    );

  return (
    <div className="mx-auto px-4 py-6">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-800">
            Pet Service Orders
          </h1>
          <p className="text-gray-500 text-sm mt-1">
            Manage and track all pet service orders
          </p>
        </div>

        <div className="flex items-center space-x-3">
          <ToggleButtonGroup
            value={currentView}
            exclusive
            sx={{ backgroundColor: "#f5f7fa", borderRadius: 12, p: 0.5 }}
          >
            <StyledToggleButton
              value="add"
              onClick={() => {
                setOpenAddDialog(true);
                setCurrentView("add");
              }}
            >
              Add Service Order
            </StyledToggleButton>

            <StyledToggleButton
              value="calendar"
              onClick={() => setCurrentView("calendar")}
            >
              Calendar View
            </StyledToggleButton>

            <StyledToggleButton
              value="list"
              onClick={() => setCurrentView("list")}
            >
              List View
            </StyledToggleButton>
          </ToggleButtonGroup>
        </div>
      </div>

      {currentView === "list" && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 p-4">
          <div className="flex items-center gap-2 mb-4 mt-4 flex-wrap">
            {/* Filter Field Selector */}
            <select
              value={searchField}
              onChange={(e) => {
                setSearchField(e.target.value as any);
                setSearchValue("");
                setFromDateValue("");
                setToDateValue("");
              }}
              className="border px-2 py-1 rounded"
            >
              <option value="searchQuery">Pet / Parent / Service Name</option>
              <option value="orderId">Order ID</option>
              <option value="serviceProviderId">Service Provider</option>
              <option value="parentId">Pet Owner</option>
              <option value="statusSlug">Service Status</option>
              <option value="dateRange">Date Range</option>
            </select>

            {/* Conditional Inputs */}
            {searchField === "dateRange" ? (
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={fromDateValue ? new Date(fromDateValue) : null}
                  onChange={(date) =>
                    setFromDateValue(
                      date ? date.toISOString().split("T")[0] : ""
                    )
                  }
                  slotProps={{
                    textField: {
                      className: "border px-2 py-1 rounded",
                      size: "small",
                    },
                  }}
                />
                <DatePicker
                  label="To Date"
                  value={toDateValue ? new Date(toDateValue) : null}
                  onChange={(date) =>
                    setToDateValue(date ? date.toISOString().split("T")[0] : "")
                  }
                  slotProps={{
                    textField: {
                      className: "border px-2 py-1 rounded",
                      size: "small",
                    },
                  }}
                />
              </LocalizationProvider>
            ) : searchField === "statusSlug" ? (
              <select
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border px-2 py-1 rounded"
              >
                <option value="">All Status</option>
                {[
                  { id: "created", name: "Created" },
                  {
                    id: "awaiting_serviceprovider_confirmation",
                    name: "Awaiting Service Provider Confirmation",
                  },
                  { id: "booking_confirmed", name: "Booking Confirmed" },
                  {
                    id: "service_accepted_unpaid",
                    name: "Service Accepted Unpaid",
                  },
                  { id: "paid_confirmed", name: "Paid Confirmed" },
                  { id: "reschedule_requested", name: "Reschedule Requested" },
                  { id: "reschedule_declined", name: "Reschedule Declined" },
                  { id: "inprogress", name: "Service Inprogress" },
                  { id: "completed", name: "Completed" },
                  { id: "no_show_petowner", name: "No Show Petowner" },
                  {
                    id: "no_show_serviceprovider",
                    name: "No Show Serviceprovider",
                  },
                  { id: "cancelled_petowner", name: "Cancelled PetOwner" },
                  {
                    id: "cancelled_serviceprovider",
                    name: "Cancelled Serviceprovider",
                  },
                  { id: "expired", name: "Expired" },
                  { id: "refund_initiated", name: "Refund Initiated" },
                  { id: "refund_completed", name: "Refund Completed" },
                  { id: "settlement_pending", name: "Settlement Pending" },
                  {
                    id: "settlement_inprogress",
                    name: "Settlement Inprogress",
                  },
                  { id: "settled", name: "Settled" },
                ].map((status) => (
                  <option key={status.id} value={status.id}>
                    {status.name}
                  </option>
                ))}
              </select>
            ) : searchField === "serviceProviderId" ? (
              <select
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border px-2 py-1 rounded"
              >
                <option value="">All Service Providers</option>
                {serviceProviders.map((provider) => (
                  <option key={provider.id} value={provider.id}>
                    {provider.user
                      ? `${provider.user.first_name} ${
                          provider.user.last_name
                        } (${provider.user.mobile || "No mobile"})`
                      : `Provider ${provider.id}`}
                  </option>
                ))}
              </select>
            ) : searchField === "parentId" ? (
              <select
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border px-2 py-1 rounded"
              >
                <option value="">All Pet Owners</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.user_id}>
                    {patient.user
                      ? `${patient.user.first_name} ${
                          patient.user.last_name
                        } (${patient.user.mobile || "No mobile"})`
                      : `Pet Owner ${patient.user_id}`}
                  </option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder={`Search by ${searchField}`}
                value={searchValue}
                onChange={(e) => setSearchValue(e.target.value)}
                className="border px-2 py-1 rounded flex-1"
                onKeyDown={(e) => {
                  if (e.key === "Enter") fetchOrders();
                }}
              />
            )}

            {/* Search Button */}
            <button
              onClick={() => fetchOrders()}
              className="bg-blue-600 text-white px-3 py-1 rounded"
              disabled={
                searchField === "dateRange" && (!fromDateValue || !toDateValue)
              }
            >
              Search
            </button>

            {/* Clear Button */}
            <button
              onClick={() => {
                setSearchValue("");
                setSearchField("searchQuery");
                setFromDateValue("");
                setToDateValue("");
                setPage(0);
                setRowsPerPage(10);
                fetchOrders("", "searchQuery");
              }}
              className="bg-red-400 text-white px-3 py-1 rounded"
            >
              Clear
            </button>
          </div>

          <DynamicTable
            columns={columns}
            data={orders}
            loading={loading}
            actions={actions}
            page={page}
            error={error}
            rowsPerPage={rowsPerPage}
            totalCount={totalCount}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            ActionsComponent={TablePaginationActions}
          />
        </div>
      )}

      {currentView === "calendar" && (
        <div className="bg-white rounded-2xl shadow-lg overflow-hidden border border-gray-100 p-4">
          <FullCalendar
            plugins={[dayGridPlugin, timeGridPlugin, interactionPlugin]}
            initialView="dayGridMonth"
            headerToolbar={{
              left: "prev,next today",
              center: "title",
              right: "dayGridMonth,timeGridWeek,timeGridDay",
            }}
            buttonText={{
              today: "Today",
              month: "Month",
              week: "Week",
              day: "Day",
            }}
            selectable
            editable
            initialDate={new Date()}
            events={events}
            eventClick={(info) => {
              const clickedOrder = orders.find(
                (order) => order.id.toString() === info.event.id.toString()
              );
              if (clickedOrder) {
                handleShowDetails(clickedOrder);
              }
            }}
            dateClick={(info) => console.log("Date clicked:", info.dateStr)}
            eventContent={(arg) => {
              const formattedTime = formatDate(arg.event.start!, {
                hour: "numeric",
                minute: "2-digit",
                hour12: true,
              });
              return (
                <div style={{ color: arg.event.textColor }}>
                  <b>{formattedTime}</b> {arg.event.title}
                </div>
              );
            }}
          />
        </div>
      )}

      <PetOrderDialog
        open={openEditDialog}
        type="edit"
        onClose={() => setOpenEditDialog(false)}
        order={editingOrder}
        onSave={handleSaveOrder}
      />

      <PetOrderDialog
        open={openAddDialog}
        type="add"
        onClose={() => {
          setOpenAddDialog(false);
          setCurrentView("list");
        }}
        order={null}
        onSave={handleSaveOrder}
      />
    </div>
  );
};

export default PetServices;
